/**
 * archModel — wo auf der Architekturseite was steht.
 *
 * Die sechs Etagen des Hauses sind sechs Spalten à 208 px in EINEM horizontal
 * scrollbaren Grid (design-hig.md, Abschnitt ②). Alles, was dafür gerechnet
 * wird — Bahn-Zuordnung, Bauzustand, Aggregat, Kanten, Platzierungsnachweis,
 * Umbruchpunkt — steht hier und nicht in einer Komponente, damit es ohne DOM
 * testbar ist.
 *
 * Zwei Regeln, die diese Datei durchhält:
 *
 *  1. **Form = Bauzustand.** Der Atlassian-Ziel-Status überschreibt ihn NICHT
 *     (bewusste Verhaltensänderung gegenüber dem alten `House.tsx`): bei 27 von
 *     31 Zielen auf `at_risk` färbte das die halbe Plattform orange, obwohl
 *     z. B. 6 von 9 Generatoren schlicht Stubs sind. Das Ziel ist ein eigener
 *     schmaler Marker am rechten Knotenrand.
 *  2. **Keine Komponente verschwindet.** Die Bahnen füllen sich aus dem Feld
 *     `lane` (von AP-1 gepflegt), die Querschnitts-Balken aus `ARCH_MERGES`.
 *     `unplacedComponentIds()` ist der Nachweis darüber und wird getestet.
 */
import type { HouseStatus } from "@/types";
import { ALL, type FlowState, type LaneId, type PipelineLane } from "@/types/pipeline";
import { tallyStates } from "@/components/flow";
import { HOUSE_COMPONENTS, type HouseComponent } from "./houseData";

/* ── Geometrie und Umbruchpunkt ─────────────────────────────────────────────
 * Die Zahlen stehen so auch in `house.css` (`.arch-grid`); ein Test in
 * `__tests__/houseCss.test.ts` liest sie dort heraus und vergleicht sie mit
 * diesen Konstanten, damit CSS und Umbruchpunkt nicht auseinanderlaufen.
 *
 * GEMESSEN, nicht geraten: 6 × 208 px + 5 × 26 px Fuge = 1.378 px. So breit muss
 * die INHALTSfläche sein, damit die sechs Bahnen nebeneinander stehen können.
 * Die App gibt ihr das nicht: `.page` in `styles/global.css` deckelt bei
 * 1.280 px und zieht 2 × 24 px Polster ab → höchstens 1.232 px, auf einem
 * 1.440-px-MacBook (Sidebar 248 px + 2 × 16 px Shell-Polster) sogar nur
 * ~1.100 px. Unterhalb von `ARCH_MIN_WIDTH` bricht die Seite deshalb um, statt
 * quer zu scrollen. */
export const ARCH_COLUMNS = 6;
export const ARCH_COL_WIDTH = 208;
export const ARCH_COL_GAP = 26;

/** Mindestbreite des Bahn-Grids in px — darunter trägt die Nebeneinander-Lage nicht. */
export const ARCH_MIN_WIDTH = ARCH_COLUMNS * ARCH_COL_WIDTH + (ARCH_COLUMNS - 1) * ARCH_COL_GAP;

export type ArchLayout = "wide" | "narrow";

/**
 * Die Regel hinter dem Verhalten: passt das Grid in die gemessene Breite, steht
 * es nebeneinander; sonst untereinander. Eine Breite von 0 heißt „noch nicht
 * gemessen" (jsdom hat keine Layout-Engine) und bleibt bewusst `wide` — ein
 * ungemessener Wert darf keinen Umbruch auslösen.
 */
export function archLayout(width: number): ArchLayout {
  if (!width) return "wide";
  return width >= ARCH_MIN_WIDTH ? "wide" : "narrow";
}

/* ── Bauzustand ─────────────────────────────────────────────────────────────
 * Dieselben fünf Formen wie im Laufzustand, anderes Wort (`vocabulary="build"`):
 * ok = in Betrieb · running = im Aufbau · sealed = extern, fremdbestimmt ·
 * open = Stub, vorgesehen und leer.
 *
 * Abweichung von `scripts/lib/pipeline.ts` (`partial → open`) ist Absicht: dort
 * geht es um den LAUFzustand eines Stands („dieser Stand hat den Knoten nicht
 * erreicht"), hier um den BAUzustand („teilweise gebaut" = im Aufbau). */
const BUILD_STATE: Record<HouseStatus, FlowState> = {
  active: "ok",
  partial: "running",
  external: "sealed",
  stub: "open",
  planned: "open",
};

export function buildState(component?: HouseComponent): FlowState {
  if (!component) return "open";
  return BUILD_STATE[component.status] ?? "open";
}

/** Bauzustand per ID — für Kanten, Dialog und Tests. */
export function buildStateOf(id: string): FlowState {
  return buildState(HOUSE_COMPONENTS[id]);
}

/* ── Bahnen ─────────────────────────────────────────────────────────────── */

/**
 * Titel + Untertitel je Bahn. Der Untertitel bleibt bewusst die Etage des
 * Hauses — die Architekturseite verliert die Haus-Metapher nicht, sie legt sie
 * nur um 90°.
 */
const LANE_DEFS: { id: LaneId; title: string; sub: string }[] = [
  { id: "sources", title: "Externe Quellen", sub: "Fundament" },
  { id: "mirror", title: "Mirror", sub: "Keller" },
  { id: "knowledge", title: "Knowledge", sub: "Erdgeschoss" },
  { id: "generators", title: "Generatoren", sub: "1. Obergeschoss" },
  { id: "output", title: "Ausgabe", sub: "Dach" },
  { id: "deployment", title: "Deployment", sub: "Firstkappe" },
];

export interface ArchLane {
  id: LaneId;
  title: string;
  sub: string;
  members: HouseComponent[];
  /**
   * Bahnkopf im Vertrag von `LaneHeader`: Kennzahl = Zahl der Knoten,
   * Aggregat = deren Bauzustände. Die Architektur kennt keinen Stand-Umschalter,
   * deshalb steht alles unter dem Schlüssel `all`.
   */
  head: PipelineLane;
}

/**
 * Die Reihenfolge innerhalb einer Bahn ist die KATASTER-Reihenfolge
 * (Deklarationsreihenfolge in `HOUSE_COMPONENTS`) — bewusst NICHT nach
 * Bauzustand sortiert.
 *
 * Diese Seite ist eine Landkarte: man lernt, wo etwas liegt. Ein Knoten, der
 * wandert, sobald sich sein Zustand ändert, nimmt genau das wieder weg. Es ist
 * dieselbe Regel „ein Knoten, eine feste Position", mit der design-hig.md
 * (Risiko 4) die neun Generator-Knoten gegen das Falten verteidigt — eine
 * zustandsabhängige Sortierung weicht sie nur an anderer Stelle auf.
 * Für die Triage tragen der Aggregat-Ring im Bahnkopf und die Form am Knoten;
 * die Position muss dabei nicht mithelfen.
 */
export const ARCH_LANES: ArchLane[] = LANE_DEFS.map((def) => {
  const members = Object.values(HOUSE_COMPONENTS).filter((c) => c.lane === def.id);
  return {
    ...def,
    members,
    head: {
      id: def.id,
      label: def.title,
      sub: def.sub,
      metricLabel: "Knoten",
      metric: { [ALL]: members.length },
      aggregate: { [ALL]: tallyStates(members.map(buildState)) },
    },
  };
});

/* ── Querschnitts-Balken ────────────────────────────────────────────────────
 * „Merged" heißt: ein Knoten belegt mehrere Grid-Spalten. Damit entfallen die
 * abgesetzten Seitenschienen des alten Hauses — und `maco-runner` wird
 * überhaupt erst sichtbar (stand in den Daten, wurde von der hartkodierten
 * Etagenliste nie gerendert).
 *
 * `from`/`to` sind Grid-Linien (`grid-column: from / to`), `to` also exklusiv. */
export interface ArchMerge {
  id: string;
  role: string;
  from: number;
  to: number;
  row: number;
}

export const ARCH_MERGES: ArchMerge[] = [
  {
    // feedsInto: "Alle anderen Repos als Referenz" — Governance über allem.
    id: "maco-meta",
    role: "Governance über alle Schichten — Kataster, Landkarte, Schemas",
    from: 1,
    to: 7,
    row: 2,
  },
  {
    // dependsOn maco-knowledge-collection (Bahn 3), bedient die Generator-Ebene.
    id: "maco-knowledge-mcp",
    role: "Querschnitt: Knowledge als Werkzeuge für KI-Agenten",
    from: 3,
    to: 5,
    row: 4,
  },
  {
    // runStage-Kette generate_* (Bahn 4) → process_repo (5) → camunda_deploy (6).
    id: "maco-runner",
    role: "Orchestriert generate_* → process_repo → camunda_deploy",
    from: 4,
    to: 7,
    row: 5,
  },
  {
    // Spann aus den eigenen Daten: die Bahnen der auflösbaren `dependsOnIds`
    // reichen von maco-knowledge-collection (Bahn 3) bis maco-validator (Bahn 6);
    // maco-meta und maco-runner sind selbst Querschnitt.
    id: "status-webapp",
    role: "Diese Anwendung — liest Knowledge, zeigt Runs, Ausgabe und Deployment",
    from: 3,
    to: 7,
    row: 6,
  },
];

/** „Bahn 3–4" — steht als Klartext im Balken und in seinem `aria-label`. */
export function spanLabel(merge: ArchMerge): string {
  return merge.from === merge.to - 1
    ? `Bahn ${merge.from}`
    : `Bahn ${merge.from}–${merge.to - 1}`;
}

/** Die Titel der überspannten Bahnen, in Bahn-Reihenfolge. */
export function spanLaneTitles(merge: ArchMerge): string[] {
  return LANE_DEFS.slice(merge.from - 1, merge.to - 1).map((d) => d.title);
}

/**
 * Die Spannweite in Worten — „Bahn 3–6 · Knowledge bis Deployment".
 *
 * Im breiten Layout sagt die überspannte Spaltenzahl selbst, was der Balken
 * überspannt. Im schmalen Layout stehen die Bahnen untereinander, es gibt keine
 * überspannten Spalten mehr — dann muss der Balken es SAGEN, sonst sähe er aus
 * wie ein normaler Knoten und die Aussage wäre weg. Derselbe Satz steht im
 * `aria-label` (auch breit), denn für Screenreader war die Geometrie noch nie
 * lesbar.
 */
export function spanSentence(merge: ArchMerge): string {
  const titles = spanLaneTitles(merge);
  if (titles.length === 0) return spanLabel(merge);
  const names = titles.length === 1 ? titles[0] : `${titles[0]} bis ${titles[titles.length - 1]}`;
  return `${spanLabel(merge)} · ${names}`;
}

/**
 * Dieselbe Spannweite als Rasterbild: ein Eintrag je Bahn, `true` = überspannt.
 * Trägt im schmalen Layout die kleine Schiene am Balken — die Geometrie bleibt
 * damit sichtbar, auch wenn sie nicht mehr aus der Breite ablesbar ist.
 */
export function spanCoverage(merge: ArchMerge): boolean[] {
  return LANE_DEFS.map((_, i) => i + 1 >= merge.from && i + 1 < merge.to);
}

/* ── Reihenfolge im Dokument ────────────────────────────────────────────────
 * Das breite Layout platziert JEDEN Knoten ausdrücklich (`grid-column`/
 * `grid-row`), die Dokumentreihenfolge ist dort also frei. Das schmale Layout
 * hat keine Platzierung mehr — dort IST die Dokumentreihenfolge die Lesefolge.
 * Deshalb steht sie hier als Datum und nicht als Reihenfolge zweier `map`s in
 * der Komponente: Jeder Balken kommt vor der ersten Bahn, die er überspannt
 * („was jetzt folgt, deckt dieser Balken ab"), danach die Bahn selbst.
 *
 * Nebeneffekt im breiten Layout: der Tastaturpfad läuft jetzt bahnweise
 * (Kopf → seine Knoten → nächster Kopf) statt erst durch alle sechs Köpfe. */
export type ArchSlot =
  | { kind: "merge"; merge: ArchMerge }
  | { kind: "lane"; lane: ArchLane; index: number };

function buildSequence(): ArchSlot[] {
  const out: ArchSlot[] = [];
  const placed = new Set<string>();
  ARCH_LANES.forEach((lane, i) => {
    for (const merge of ARCH_MERGES) {
      if (merge.from === i + 1) {
        out.push({ kind: "merge", merge });
        placed.add(merge.id);
      }
    }
    out.push({ kind: "lane", lane, index: i });
  });
  // Ein Balken, dessen `from` außerhalb der Bahnen läge, fiele sonst still aus
  // dem schmalen Layout — dieselbe Sorte Verlust, die ARCH_MERGES beseitigt hat.
  for (const merge of ARCH_MERGES) {
    if (!placed.has(merge.id)) out.push({ kind: "merge", merge });
  }
  return out;
}

export const ARCH_SEQUENCE: ArchSlot[] = buildSequence();

/* ── Platzierungsnachweis ───────────────────────────────────────────────── */

const PLACED = new Set<string>([
  ...ARCH_LANES.flatMap((l) => l.members.map((c) => c.id)),
  ...ARCH_MERGES.map((m) => m.id),
]);

/** Komponenten aus `houseData.ts`, die die Seite NICHT zeigt. Muss leer sein. */
export function unplacedComponentIds(): string[] {
  return Object.keys(HOUSE_COMPONENTS).filter((id) => !PLACED.has(id));
}

/** Zahl der platzierten Knoten (Bahn-Knoten + Querschnitts-Balken). */
export const PLACED_COUNT = PLACED.size;

/* ── Kanten ─────────────────────────────────────────────────────────────────
 * Quelle sind die ID-Felder aus AP-1 — `feedsIntoIds` vorwärts, `dependsOnIds`
 * rückwärts. Beides ist dieselbe Relation aus zwei Blickrichtungen; ohne die
 * Rückrichtung fehlten z. B. alle drei Quellen → Scraper-Kanten, die nur beim
 * Scraper als `dependsOnIds` stehen.
 *
 * `derived: true` = aus Freitext heuristisch aufgelöst → gestrichelt gezeichnet
 * und im sichtbaren Zähler mitgezählt. Steht dieselbe Kante irgendwo als echte
 * ID-Referenz, gewinnt „aufgelöst". */
export interface ArchEdge {
  from: string;
  to: string;
  derived: boolean;
}

function collectEdges(): ArchEdge[] {
  const map = new Map<string, ArchEdge>();
  const add = (from: string, to: string, derived: boolean) => {
    if (from === to || !HOUSE_COMPONENTS[from] || !HOUSE_COMPONENTS[to]) return;
    const key = `${from}>${to}`;
    const prev = map.get(key);
    if (prev) {
      if (!derived) prev.derived = false;
      return;
    }
    map.set(key, { from, to, derived });
  };
  for (const c of Object.values(HOUSE_COMPONENTS)) {
    for (const l of c.feedsIntoIds ?? []) add(c.id, l.id, !!l.derived);
    for (const l of c.dependsOnIds ?? []) add(l.id, c.id, !!l.derived);
  }
  return [...map.values()];
}

/** Alle aufgelösten Referenzen des Katasters. */
export const ARCH_EDGES: ArchEdge[] = collectEdges();

const isLaned = (id: string) => !!HOUSE_COMPONENTS[id]?.lane;

/**
 * Gezeichnet werden nur Kanten zwischen Knoten mit fester Bahn-Position.
 *
 * Ein Querschnitts-Balken hat keine einzelne Stelle im Fluss — er spannt drei
 * bis sechs Bahnen. Eine Kante dorthin müsste an einem willkürlichen Punkt
 * enden und liefe quer zurück durch das Bild. Diese Beziehungen stehen deshalb
 * im Dialog („Benötigt" / „Liefert an"), nicht im Overlay; `CROSS_EDGES` zählt
 * sie sichtbar mit, damit nichts still verschwindet.
 */
export const LANE_EDGES: ArchEdge[] = ARCH_EDGES.filter((e) => isLaned(e.from) && isLaned(e.to));
export const CROSS_EDGES: ArchEdge[] = ARCH_EDGES.filter((e) => !isLaned(e.from) || !isLaned(e.to));
